/**
 * Creates a range of integers using the supplied start and end.
 * If the end is greater than the start, a descending range will
 * be generated.
 *
 * @param start The start of the range (valid integer)
 * @param end The end of the range (valid integer)
 *
 * @example
 * createRange(-2, 2) // => [ -2, -1, 0, 1, 2 ]
 * createRange(2, -2) // => [ 2, 1, 0, -1, -2 ]
 * createRange(2, 2) // => [ 2 ]
 */
export function createRange(start: number, end: number): number[] {
    if (!Number.isInteger(start)) {
        throw new Error('Start of range must be an integer');
    }
    if (!Number.isInteger(end)) {
        throw new Error('End of range must be an integer');
    }

    if (start === end) {
        return [start];
    }

    const ascending = end > start;
    const size = Math.abs(end - start);

    const arr = Array(size + 1);
    if (ascending) {
        for (let i = start; i <= end; i++) {
            arr[i - start] = i;
        }
    } else {
        for (let i = start; i >= end; i--) {
            arr[start - i] = i;
        }
    }

    return arr;
}

/**
 * Compares two sets to see if they contain the same values.
 *
 * @param s1 The first set.
 * @param s2 The second set.
 */
export function setsAreSame<T>(s1: Set<T>, s2: Set<T>): boolean {
    return s1.size === s2.size && Array.from(s1).every((v) => s2.has(v));
}

/**
 * Compares two arrays to see if they contain the same values, with order of items ignored. Note does strict equals comparison.
 *
 * @param array1 The first array.
 * @param array2 The second array.
 */
export function arraysAreSame<T>(array1: T[], array2: T[]): boolean {
    return (
        array1.length === array2.length &&
        setsAreSame<T>(new Set(array1), new Set(array2))
    );
}

/**
 * Compares two arrays to see if they contain the same values in the same order.
 *
 * @param array1 The first array.
 * @param array2 The second array.
 */
export function arraysAreEqual<T>(array1: T[], array2: T[]): boolean {
    return (
        array1.length === array2.length &&
        array1.every((item, index) => item === array2[index])
    );
}

/**
 * Returns a new array containing the unique values of the supplied array.
 *
 * @param array The initial array.
 */
export function unique<T>(array: T[]): T[] {
    return Array.from(new Set(array));
}

/**
 * Returns a new array containing unique items according to the supplied criteria.
 *
 * @param array The initial array.
 * @param by The criteria for determining if items are unique.
 *
 * @example
 * uniqueBy(indicators, ({ Id }) => Id)
 * uniqueBy(people, ({ firstName }) => firstName, ({ lastName }) => lastName)
 */
export function uniqueBy<T>(array: T[], ...by: ((item: T) => unknown)[]): T[] {
    if (by.length === 0) {
        throw new Error('Unique by criteria required');
    }
    if (array.length < 2) {
        return [...array];
    }
    const getByValues = (item: T) => by.map((fn) => fn(item));

    return array
        .reduce<{ item: T; byValues: unknown[] }[]>((results, item) => {
            const byValues = getByValues(item);
            const anyEq = results.some((result) =>
                arraysAreEqual(result.byValues, byValues)
            );
            if (!anyEq) {
                results.push({ item, byValues });
            }
            return results;
        }, [])
        .map(({ item }) => item);
}

/**
 * Returns the last item from the supplied array.  Returns `undefined` when
 * supplied array is empty.
 *
 * @param array The array.
 */
export function lastItem<TItem>(array: TItem[]): TItem | undefined {
    return array.length === 0 ? undefined : array[array.length - 1];
}

/**
 * Returns a new array containing the first `count` of items.
 *
 * @param array The array to take items from.
 * @param count The number items to take.
 * @param skip The number of items to skip before taking items (from head of `array`).
 * @example
 * take([1, 2, 3, 4], 2) === [1, 2] // take 2 items
 * take([1, 2, 3, 4], 2, 1) === [2, 3] // skip 1 item, take 2 items
 *
 */
export function take<T>(array: T[], count: number, skip = 0) {
    return array.slice(skip, count + skip);
}

/**
 * Returns a new array containing the last `count` of items.
 * Note: Items remain in the currently supplied order.
 *
 * @param array The array to take items from.
 * @param count The number items to take.
 * @param skip The number of items to skip before taking items (from tail of `array`).
 * @example
 * takeLast([1, 2, 3, 4], 2) === [3, 4] // take 2 items
 * takeLast([1, 2, 3, 4], 2, 1) === [2, 3] // skip 1 item, take 2 items
 */
export function takeLast<T>(array: T[], count: number, skip = 0) {
    return array.slice(Math.max(0, array.length - count - skip), array.length - skip);
}

/**
 *
 * @param items The array to take items from.
 * @param predicate Items will be taken until the predicate returns false.
 * @param inclusive When true, the item in which the predicate returned
 *                  false will be included (defaults to `false`).
 * @returns
 */
export function takeWhile<TItem>(
    items: TItem[],
    predicate: (value: TItem, index: number, obj: TItem[]) => boolean,
    inclusive = false
): TItem[] {
    const endIndex = items.findIndex(
        (val, idx, arr) => predicate(val, idx, arr) === false
    );
    return endIndex === -1 ? items : items.slice(0, inclusive ? endIndex + 1 : endIndex);
}

/**
 * Returns a new array that excludes the last items of the supplied `count`.
 * Note: Items remain in the currently supplied order.
 *
 * @param array The array to take items from.
 * @param count The number items to exclude from the end.
 * @example
 * skipLast([1, 2, 3, 4], 2) === [1, 2] // skip last 2 items
 */
export function skipLast<T>(array: T[], count: number) {
    return array.slice(0, Math.max(0, array.length - count));
}

/**
 * Transforms an array into a Map.
 *
 * @param array The array to transform into a map.
 * @param keyFn Function to get the map key for the item.
 */
export function toMap<T, K, V>(
    array: T[],
    keyFn: (value: T) => K,
    valueFn: (value: T) => V
): Map<K, V> {
    return new Map<K, V>(array.map((value) => [keyFn(value), valueFn(value)]));
}

export type ComparableType = string | number | Date | null | undefined;

export type SortKey<T> = (item: T) => ComparableType;

export type SortConfig<T> =
    | SortKey<T>
    | Readonly<{
          key: SortKey<T>;
          dir?: 'asc' | 'desc' | 'ascNullsFirst' | 'descNullsLast';
      }>;

/**
 * Utility for `compare` that normalizes values by using the `valueOf` of the supplied value.
 * Return `null` if:
 * * The supplied value is `undefined`.
 * * The `valueOf` is `NaN`.
 */
function normalizeForCompare(value: ComparableType): string | number | null {
    const valueOf = value?.valueOf() ?? null;

    return valueOf === null || (typeof valueOf === 'number' && Number.isNaN(valueOf))
        ? null
        : valueOf;
}

/**
 * Compares 2 values and returns:
 * * `-1` when `value1 < value2`
 * * `0` when `value1 === value2`
 * * `1` when `value1 > value2`
 *
 * `undefined` and `NaN` values are both treated as `null` values and:
 * *  `null value < non null value`
 *
 * @param value1 The first value for comparison
 * @param value2 The second value for comparison
 * @param nullsFirst When `true`, null values will be considered less than non-null values.
 */
export function compare(
    value1: ComparableType,
    value2: ComparableType,
    nullsFirst: boolean
): number {
    const v1 = normalizeForCompare(value1);
    const v2 = normalizeForCompare(value2);

    if (v1 === null && v2 === null) {
        return 0;
    }
    if (v1 === null && v2 !== null) {
        return nullsFirst ? -1 : 1;
    }
    if (v1 !== null && v2 === null) {
        return nullsFirst ? 1 : -1;
    }
    if (typeof v1 !== typeof v2) {
        return 0;
    }
    // Check here to keep TypeScript happy.
    if (v1 !== null && v2 !== null) {
        if (v1 < v2) {
            return -1;
        }
        if (v1 > v2) {
            return 1;
        }
    }
    return 0;
}

/**
 * Returns a sorted array using the supplied sorting config.
 * If only a sort key function is supplied, sort direction defaults to `asc`.
 * The supplied array is not mutated.
 *
 * ### Sort Directions:
 * * `asc` - sorts least to greatest (nulls last): `1`, `2`, `null`
 * * `desc` - sorts greatest to least (nulls first): `null`, `2`, `1`
 * * `ascNullsFirst` - sorts least to greatest (nulls first): `null`, `1`, `2`
 * * `descNullsLast` - sorts greatest to least (nulls last): `2`, `1`, `null`
 *
 * @param array The array to sort.
 * @param sortConfig The function that provide values for sorting.
 *
 * ### Examples:
 * @example
 *
 * // Single sort:
 * sortBy(people, (p) => p.lastName); // ascending
 * sortBy(people, { key: (p) => p.lastName, dir: 'asc' }); // ascending
 * sortBy(people, { key: (p) => p.lastName, dir: 'desc' }); // descending
 *
 * // Multi-sort:
 * sortBy(
 *     people,
 *     (p) => p.lastName.toLowerCase(), // ascending
 *     { key: (p) => p.firstName.toLowerCase(), dir: 'asc' }, // ascending
 *     { key: (p) => p.dateOfBirth, dir: 'desc' }, // descending
 * );
 *
 */
export function sortBy<T>(array: T[], ...sortConfigs: SortConfig<T>[]): T[] {
    return [...array].sort((item1, item2) => {
        // Using for loop to allow early return
        for (let i = 0; i < sortConfigs.length; i += 1) {
            const cfg = sortConfigs[i];
            const { key, dir } = typeof cfg === 'object' ? cfg : { key: cfg, dir: 'asc' };
            const nullsFirst = dir === 'ascNullsFirst' || dir === 'descNullsLast';
            const comp = compare(key(item1), key(item2), nullsFirst);
            const sortAsc = dir === 'asc' || dir === 'ascNullsFirst';
            if (comp !== 0) {
                return sortAsc ? comp : comp * -1;
            }
        }
        return 0;
    });
}

/**
 * Transforms an array into an an object where items are grouped by the result of the
 * supplied grouping function.  The returned object will have its keys inferred based
 * on the return type of the grouping function.
 *
 * @param items The items to be grouped.
 * @param groupFn A grouping function that returns the name of the group for each item.
 * @returns The group object.
 *
 * ### Examples:
 * @example
 * const grouped = groupBy(
 *     [
 *         { last: 'Smith', first: 'Tim' },
 *         { last: 'Foster', first: 'Jane' },
 *         { last: 'Smith', first: 'Jane' },
 *         { last: 'Foster', first: 'Dave' },
 *     ],
 *     (person) => person.last
 * );
 *
 * expect(grouped).toEqual({
 *     Smith: [
 *         { last: 'Smith', first: 'Tim' },
 *         { last: 'Smith', first: 'Jane' },
 *     ],
 *     Foster: [
 *         { last: 'Foster', first: 'Jane' },
 *         { last: 'Foster', first: 'Dave' },
 *     ],
 * });
 *
 * @example
 * const grouped = groupBy(
 *     [1, 2, 3, 4],
 *     (num) => num % 2 === 0 ? 'even' : 'odd'
 * );
 *
 * // Keys are inferred from grouping function
 * expect(grouped.even).toEqual([2, 4]);
 * expect(grouped.odd).toEqual([1, 3]);
 */
export function groupBy<TGroup extends string, TValue>(
    items: TValue[],
    groupFn: (item: TValue) => TGroup
): Partial<{ [key in TGroup]?: TValue[] }> {
    return items.reduce<Partial<{ [key in TGroup]?: TValue[] }>>((result, item) => {
        const groupName = groupFn(item);
        return {
            ...result,
            [groupName]: [...(result[groupName] ?? []), item],
        };
    }, {});
}

/**
 * Filters an array of items and returns a new array containing only
 * items that are not null or undefined.
 *
 * @param items The items to filter.
 * @returns A new array containing only items that are not null or undefined.
 */
export function excludeNullAndUndefined<TItem>(
    items: (TItem | null | undefined)[]
): Exclude<TItem, null | undefined>[] {
    return items.filter((item) => item !== null && item !== undefined) as Exclude<
        TItem,
        null | undefined
    >[];
}

/**
 * Groups array of items into an array of arrays of the specified size.
 * If the array size is not evenly divisible by the supplied size,
 * the final group size will be less than the supplied size.
 *
 * @param items The items to group.
 * @param size The size of the group.
 *
 * @example
 * inGroupsOf([1, 2, 3, 4], 1) // => [[1], [2], [3], [4]]
 * inGroupsOf([1, 2, 3, 4], 2) // => [[1, 2], [3, 4]]
 * inGroupsOf([1, 2, 3, 4], 3) // => [[1, 2, 3], [4]]
 * inGroupsOf([1, 2, 3, 4], 4) // => [[1, 2, 3, 4]]
 * inGroupsOf([1, 2, 3, 4], 5) // => [[1, 2, 3, 4]]
 */
export function inGroupsOf<TItem>(items: TItem[], size: number): TItem[][] {
    if (size < 1) {
        throw new Error('Size must be a positive integer');
    }
    if (items.length === 0) {
        return [];
    }
    if (items.length <= size) {
        return [items];
    }

    const groups = [];
    let currentGroup = [];
    for (let i = 0; i < items.length; i++) {
        currentGroup.push(items[i]);
        if (currentGroup.length === size) {
            groups.push(currentGroup);
            currentGroup = [];
        }
    }

    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }

    return groups;
}

/**
 * Splits an array of items into two groups using the supplied split function.
 * Returns an array in which:
 *
 * * the first element contains all the items in which the split function returned `true`
 * * the second element contains all the items in which the split function returned `false`
 *
 * @param items the items to split into two groups
 * @param splitFn the function used to split the items into groups
 * @returns An array in which the first element contains all the items in which
 *          the split function returned `true` and the second element contains all
 *          the items in which the split function returned `false`.
 *
 * @example
 * const [isEven, isOdd] = splitOn([1, 2, 3, 4], (num) => num % 2 === 0); // [[2, 4], [1, 3]]
 */
export function splitOn<TItem>(
    items: TItem[],
    splitFn: (item: TItem, index: number) => boolean
): [TItem[], TItem[]] {
    const trueItems = [];
    const falseItems = [];
    for (let i = 0; i < items.length; i += 1) {
        const el = items[i];
        if (splitFn(el, i)) {
            trueItems.push(el);
        } else {
            falseItems.push(el);
        }
    }

    return [trueItems, falseItems];
}

/**
 * Supplies each item and index to the supplied callback and return the first value from
 * the callback that is not `null` or `undefined`.  If no value is found, return
 * `undefined`.
 *
 * @param items the items to iterate.
 * @param where the callback that either returns a value or `null`/`undefined`.
 * @returns either the first value from the callback or `undefined`.
 */
export function findFirstValue<TItem, TResult>(
    items: TItem[],
    where: (item: TItem, index: number) => NonNullable<TResult> | null | undefined
): NonNullable<TResult> | undefined {
    for (let index = 0; index < items.length; index += 1) {
        const result = where(items[index], index);
        if (result !== null && result !== undefined) {
            return result;
        }
    }
    return undefined;
}

/**
 * Adds or removes an item from array.  If the supplied item does not exist in the array,
 * it will be added, otherwise, it will be removed.
 *
 * @param array the array.
 * @param itemToToggle the item to add or remove (toggle).
 * @returns a new array with item added or removed.
 */
export function toggleArrayItem<TItem>(array: TItem[], itemToToggle: TItem): TItem[] {
    return array.includes(itemToToggle)
        ? array.filter((item) => item !== itemToToggle)
        : [...array, itemToToggle];
}

/**
 * Gets the first item of the array. If the array does not have any items in it,
 * undefined will be returned.
 * @param array the array.
 * @returns the first item of the array or undefined.
 */
export function firstItem<TItem>(array: TItem[]): TItem | undefined {
    return array[0];
}

/**
 * Gets the total count of items in the array matching the predicate.
 * @param array the array.
 * @param where the predicate to match.
 * @returns a number represent the count of items in the array matching the predicate.
 */
export function countBy<T>(array: T[], where: (item: T) => boolean): number {
    return array.reduce((count, item) => (where(item) ? count + 1 : count), 0);
}

/**
 * Gets the item with the minimum count value returned from the "by" callback.
 * @param array the array.
 * @param by the callback returning a "count".
 * @returns the item with the minimum value returned from the "by" callback.
 */
export function findMinBy<T>(array: T[], by: (item: T) => number): T {
    if (array.length === 0) {
        throw new Error('Array cannot be empty');
    }
    const [firstValue, ...rest] = array;
    return rest.reduce(
        (result, item) => {
            const count = by(item);
            return count < result.count ? { item, count } : result;
        },
        { item: firstValue, count: by(firstValue) }
    ).item;
}

/**
 * Replace all instances of an item in an array.
 *
 * @param array the array.
 * @param replace the item to replace.
 * @param replacement the replacement item.
 * @returns a new array with any items replaced.
 */
export function replaceItems<TItem>(
    array: TItem[],
    replace: TItem,
    replacement: TItem
): TItem[] {
    return array.map((item) => (item === replace ? replacement : item));
}

/**
 * Adds any missing items to the supplied array.
 *
 * @param array the array.
 * @param itemsToAdd the items to add (if they are not already contained in the array).
 * @returns a new array with the missing items added.
 */
export function addMissingItems<TItem>(array: TItem[], itemsToAdd: TItem[]): TItem[] {
    return [...array, ...itemsToAdd.filter((item) => !array.includes(item))];
}

/**
 * Inserts item before or after a specified index.
 *
 * @param array the array.
 * @param items the items to insert.
 * @param position the position to insert.
 * @param atIndex if position is `before` the items will be inserted before
 *                the specified index, otherwise, after the specified index.
 * @returns a new array with the items inserted.
 */
export function insertItems<TItem>(
    array: TItem[],
    items: Iterable<TItem>,
    position: 'before' | 'after',
    atIndex: number
) {
    const targetIdx = Math.max(
        Math.min(array.length, position === 'before' ? atIndex : atIndex + 1),
        0
    );
    return array.toSpliced(targetIdx, 0, ...items);
}

/**
 * Removes all the supplied items from the supplied array.
 *
 * @param array the array.
 * @param itemsToRemove the items to remove from the array.
 * @returns a new array with items removed.
 */
export function removeAllItems<TItem>(
    array: TItem[],
    itemsToRemove: Iterable<TItem>
): TItem[] {
    const uniqueItemsToRemove = new Set(itemsToRemove);
    return array.filter((item) => !uniqueItemsToRemove.has(item));
}

/**
 * Checks to see if the supplied array includes all supplied items
 * @param array The array
 * @param items The items to check for
 * @returns True if the array contains all supplied items, false otherwise
 * @example
 * arrayIncludesAll([1, 3, 2, 4, 5], [1, 2]) // true
 * arrayIncludesAll([1, 3, 2, 4], [1, 2, 6]) // false
 */
export function arrayIncludesAll<TItem>(array: TItem[], items: TItem[]) {
    return items.every((item) => array.includes(item));
}

/**
 * Used to map an array in reverse. Note: This utility works similarly to
 * `Array.reduceRight` in which the `index` supplied to the `callbackfn` will
 * be the position of the item in the supplied array.
 *
 * @param array The array.
 * @param callbackfn The mapping function.
 * @returns a new array.
 */
export function mapRight<TItem, TResult>(
    array: TItem[],
    callbackfn: (value: TItem, index: number) => TResult
): TResult[] {
    return array.reduceRight<TResult[]>((result, item, index) => {
        result.push(callbackfn(item, index));
        return result;
    }, [] as TResult[]);
}

/**
 * Returns the index of the last element in the array where predicate is true, and -1
 * otherwise.
 * @param array The source array to search in
 * @param predicate find calls predicate once for each element of the array, in descending
 * order, until it finds one where predicate returns true. If such an element is found,
 * findLastIndex immediately returns that element index. Otherwise, findLastIndex returns -1.
 */
export function findLastIndex<T>(
    array: Array<T>,
    predicate: (value: T, index: number, obj: T[]) => boolean
): number {
    for (let i = array.length - 1; i >= 0; i -= 1) {
        if (predicate(array[i], i, array)) {
            return i;
        }
    }
    return -1;
}

/**
 * Combines two arrays into an array of tuples.
 *
 * @example
 * zip([1, 2, 3], ['a', 'b', 'c']) // [[1, 'a'], [2, 'b'], [3, 'c']]
 */
export function zip<TItem1, TItem2>(
    array1: TItem1[],
    array2: TItem2[]
): [TItem1, TItem2][] {
    if (array1.length !== array2.length) {
        throw new Error('Arrays must be the same length');
    }
    return array1.map((item1, index) => [item1, array2[index]]);
}

/**
 * Simple utility to check if an array is undefined or contains no items.
 * @param array The source array to check
 * @returns a boolean indicating whether the supplied array is undefined or empty
 * @example
 * isUndefinedOrEmpty(undefined) // true
 */
export function isUndefinedOrEmpty<TItem>(array: TItem[] | undefined): boolean {
    return array === undefined || array.length === 0;
}

/**
 * Checks that all elements in `arr` are not undefined, and narrows the type
 * accordingly.
 * @param arr
 * @returns boolean
 */
export function allAreDefined<T>(arr: (T | undefined)[]): arr is T[] {
    return arr.every((v) => v !== undefined);
}

export function countIf<T>(arr: T[], predicate: (v: T) => boolean): number {
    return arr.reduce((accum, cur) => accum + (predicate(cur) ? 1 : 0), 0);
}

export function any<T extends unknown[]>(arr: T | undefined): arr is T {
    return arr !== undefined && arr.length > 0;
}

function defaultLabel<T>(item: T) {
    return `${item}`;
}

/**
 * Returns a comma separated list of items, with the last item prefixed by "and".
 * @param arr The array of items.
 * @param getLabel A function that returns the label for each item. Defaults to string representation if not supplied.
 * @returns A comma separated list of items.
 */
export function toHumanReadable<T>(
    arr: T[],
    getLabel: (item: T, index: number) => string = defaultLabel
): string {
    if (arr.length === 0) {
        return '';
    }
    if (arr.length === 1) {
        return getLabel(arr[0], 1);
    }
    if (arr.length === 2) {
        return `${getLabel(arr[0], 0)} and ${getLabel(arr[1], 1)}`;
    }

    return `${arr.slice(0, -1).map(getLabel).join(', ')}, and ${getLabel(
        arr[arr.length - 1],
        arr.length - 1
    )}`;
}
